/**
 * Error codes IAM returns when the account exists but its status blocks
 * access: a terminated (cesado) employee, a suspended one, or any account
 * left in a non active state.
 *
 * They matter because a lockout by failed attempts also answers 403. Without
 * these codes both cases look identical to the client, and the user ends up
 * reading "try again in 15 minutes" for an account that will never come back.
 */
export const ACCOUNT_TERMINATED_CODE = 'KLAB403_ACCOUNT_TERMINATED';
export const ACCOUNT_SUSPENDED_CODE = 'KLAB403_ACCOUNT_SUSPENDED';
export const ACCOUNT_DISABLED_CODE = 'KLAB403_ACCOUNT_DISABLED';

const ACCOUNT_DISABLED_CODES: readonly string[] = [
    ACCOUNT_TERMINATED_CODE,
    ACCOUNT_SUSPENDED_CODE,
    ACCOUNT_DISABLED_CODE,
];

/** Fallback shown only if IAM ever answers one of these codes with no message. */
export const ACCOUNT_DISABLED_FALLBACK_MESSAGE =
    'Tu cuenta no está habilitada para acceder al sistema. Comunícate con tu área de Recursos Humanos.';

/**
 * Tells whether an HTTP error is IAM refusing access because of the account
 * status, as opposed to wrong credentials or a temporary lockout.
 *
 * @param err the HttpErrorResponse (or any error carrying `error.code`)
 */
export function isAccountDisabledError(err: unknown): boolean {
    const code = (err as { error?: { code?: unknown } })?.error?.code;
    return typeof code === 'string' && ACCOUNT_DISABLED_CODES.includes(code);
}

/**
 * Message to show for an account-status refusal. Prefers the text IAM sent,
 * so the wording stays owned by the backend and stays in sync across the web,
 * the desktop agent and the password recovery screens.
 *
 * @param err the HttpErrorResponse (or any error carrying `error.message`)
 */
export function accountDisabledMessage(err: unknown): string {
    const message = (err as { error?: { message?: unknown } })?.error?.message;
    return typeof message === 'string' && message.trim()
        ? message.trim()
        : ACCOUNT_DISABLED_FALLBACK_MESSAGE;
}
